import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Icon } from "@iconify/react";

// ** Components
import AppModal from "@/components/modal/AppModal.tsx";
import { StudioModelForm } from "@/views/admin/studios/StudioModelForm.tsx";
import DeleteModal from "@/components/modal/DeleteModal.tsx";

// ** Hooks
import {
  useGetStudioModelsQuery,
  useCreateStudioModelMutation,
  useUpdateStudioModelMutation,
} from "@/api/studiosApi.ts";

// ** Utils
import { mutationFeedback } from "@/services/utils.tsx";
import { shouldDisplayField } from "@/utils/form.ts";

// ** Schema
import type { StudioModelSubmitPayload } from "@/schemas/studioModel.ts";

// ** Types
import type { components } from "@/types/api/generated-api-schema.ts";

type StudioModel =
  components["schemas"]["StudioModel.jsonld-studio_model.read_read"];

export const Route = createFileRoute("/admin/models")({
  component: StudioModelsPage,
});

function StudioModelsPage() {
  const { t } = useTranslation();

  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<StudioModel | null>(null);

  // API
  const { data, isLoading, error } = useGetStudioModelsQuery({});
  const [createModel, { isLoading: isCreating }] = useCreateStudioModelMutation();
  const [updateModel, { isLoading: isUpdating }] = useUpdateStudioModelMutation();
  const isSubmitting = isCreating || isUpdating;

  const handleAdd = () => {
    setSelectedModel(null);
    setFormOpen(true);
  };

  const handleEdit = (model: StudioModel) => {
    setSelectedModel(model);
    setFormOpen(true);
  };

  const handleDelete = (model: StudioModel) => {
    setSelectedModel(model);
    setDeleteOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedModel(null);
  };

  const handleModelSubmit = async (data: StudioModelSubmitPayload) => {
    let result;
    if (selectedModel?.uuid) {
      result = await updateModel({ uuid: selectedModel.uuid, data });
    } else {
      result = await createModel(data);
    }

    mutationFeedback(result, {
      success: selectedModel
        ? t("admin.models.messages.updated")
        : t("admin.models.messages.created"),
      error: selectedModel
        ? t("admin.models.messages.updateError")
        : t("admin.models.messages.createError"),
    });

    if (!("error" in result)) {
      handleFormClose();
    }
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedModel(null);
  };

  const models = data?.member ?? [];

  if (error) {
    return (
      <Box>
        <Typography color="error">{t("admin.common.loadingError")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600} color="text.primary">
          {t("admin.models.title")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon icon="tabler:plus" />}
          onClick={handleAdd}
        >
          {t("admin.models.addModel")}
        </Button>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} />
          ))}
        </Stack>
      ) : models.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" mb={2}>
            {t("admin.models.noModels")}
          </Typography>
          <Button variant="contained" onClick={handleAdd}>
            {t("admin.models.addModel")}
          </Button>
        </Card>
      ) : (
        <Stack spacing={2}>
          {models.map((model) => (
            <StudioModelCard
              key={model.uuid}
              model={model}
              onEdit={() => handleEdit(model)}
              onDelete={() => handleDelete(model)}
              t={t}
            />
          ))}
        </Stack>
      )}

      {/* Form Modal - key forces remount when switching between create/edit */}
      <AppModal
        key={selectedModel?.uuid ?? "create"}
        open={formOpen}
        onClose={handleFormClose}
        title={selectedModel ? t("admin.models.editModel") : t("admin.models.addModel")}
        sx={{ width: 700, maxHeight: "90vh", overflow: "auto" }}
      >
        <StudioModelForm
          model={selectedModel}
          onSubmit={handleModelSubmit}
          onCancel={handleFormClose}
          isSubmitting={isSubmitting}
        />
      </AppModal>

      {/* Delete Modal */}
      {selectedModel && (
        <DeleteModal
          open={deleteOpen}
          onClose={handleDeleteClose}
          elementLabel={selectedModel.name}
          elementIri={`/api/studio_models/${selectedModel.uuid}`}
          onSuccess={handleDeleteClose}
          additionalText={
            selectedModel.studiosCount && selectedModel.studiosCount > 0
              ? t("admin.models.deleteWarning", {
                  count: selectedModel.studiosCount,
                })
              : undefined
          }
        />
      )}
    </Box>
  );
}

// StudioModelCard component
type CardProps = {
  model: StudioModel;
  onEdit: () => void;
  onDelete: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
};

function StudioModelCard({ model, onEdit, onDelete, t }: CardProps) {
  // Use the computed url property from API, or fallback to path
  const mainImageUrl = model.mainImage?.url ?? (model.mainImage?.path ? `/uploads/${model.mainImage.path}` : null);

  return (
    <Card sx={{ display: "flex", alignItems: "stretch" }}>
      {/* Thumbnail */}
      {mainImageUrl ? (
        <CardMedia
          component="img"
          sx={{ width: 150, height: 120, objectFit: "cover" }}
          image={mainImageUrl}
          alt={model.name}
        />
      ) : (
        <Box
          sx={{
            width: 150,
            height: 120,
            bgcolor: "grey.200",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon icon="tabler:photo-off" width={40} color="grey" />
        </Box>
      )}

      {/* Content */}
      <CardContent sx={{ flex: 1, display: "flex", alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {model.name}
          </Typography>
          {shouldDisplayField(model.surface) && (
            <Typography variant="body2" color="text.secondary">
              {t("admin.models.surface")}: {model.surface}
            </Typography>
          )}
          {shouldDisplayField(model.basePrice) && (
            <Typography variant="body2" color="text.secondary">
              {t("admin.models.basePrice")}: {model.basePrice}€/mois
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t("admin.models.studiosCount", { count: model.studiosCount ?? 0 })}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("admin.common.edit")}>
            <IconButton onClick={onEdit}>
              <Icon icon="tabler:edit" width={20} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("admin.common.delete")}>
            <IconButton color="error" onClick={onDelete}>
              <Icon icon="tabler:trash" width={20} />
            </IconButton>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
}
